import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPhone, FiMapPin, FiClock } = FiIcons;

const Home = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const heroImages = [
    'https://i.ibb.co/xKn2jMcH/Products-1.jpg',
    'https://i.ibb.co/77JYx8p/Products-2.jpg',
    'https://i.ibb.co/VcnsdwQL/Products-3.jpg',
    'https://i.ibb.co/nqZWLtqS/Products-4.jpg',
    'https://i.ibb.co/Q39nJWP7/Products-5.jpg'
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [heroImages.length]);

  const services = [
    { icon: '🏪', title: 'Dine-in', description: 'Enjoy our cozy atmosphere' },
    { icon: '🥡', title: 'Takeout', description: 'Quick and convenient pickup' },
    { icon: '🚚', title: 'Delivery', description: 'Fresh desserts to your door' }
  ];

  const featuredProducts = [
    {
      name: 'Classic Zalabya',
      image: 'https://i.ibb.co/xKn2jMcH/Products-1.jpg',
      description: 'Traditional Egyptian fried dough balls with various toppings'
    },
    {
      name: 'Dubai Chocolate',
      image: 'https://i.ibb.co/77JYx8p/Products-2.jpg',
      description: 'Viral Dubai chocolate with premium ingredients'
    },
    {
      name: 'Strawberry Cup',
      image: 'https://i.ibb.co/VcnsdwQL/Products-3.jpg',
      description: 'Fresh strawberries with chocolate and cream'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Zalabya - Authentic Egyptian Desserts | Mississauga</title>
        <meta name="description" content="Experience the best Egyptian desserts in Mississauga. Fresh zalabya, Dubai chocolate, and traditional sweets. Dine-in, takeout & delivery available." />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Text Content with Enhanced Animation */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              <motion.h1 
                className="text-5xl lg:text-7xl font-bold mb-6"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
              >
                <motion.span 
                  className="text-yellow-400"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 1 }}
                >
                  Authentic
                </motion.span>
                <br />
                <motion.span 
                  className="text-yellow-400"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 1.2 }}
                >
                  Egyptian
                </motion.span>
                <br />
                <motion.span 
                  className="text-yellow-400"
                  initial={{ opacity: 0, rotate: -5 }}
                  animate={{ opacity: 1, rotate: 0 }}
                  transition={{ duration: 0.6, delay: 1.4 }}
                >
                  Desserts
                </motion.span>
              </motion.h1>
              
              <motion.p 
                className="text-xl text-gray-300 mb-8 leading-relaxed"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1.6 }}
              >
                Experience the magic of traditional Zalabya - crispy on the outside, 
                soft on the inside, served with premium toppings and sauces.
              </motion.p>
              
              <motion.div 
                className="flex flex-col sm:flex-row gap-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1.8 }}
              >
                <motion.a 
                  href="#menu" 
                  className="btn-primary text-center"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  View Our Menu
                </motion.a>
                <motion.a 
                  href="tel:+19058207700" 
                  className="btn-secondary text-center"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Order Now
                </motion.a>
              </motion.div>
            </motion.div>

            {/* Image Slider with Enhanced Animation */}
            <motion.div
              initial={{ opacity: 0, x: 50, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="relative"
            >
              <div className="relative overflow-hidden rounded-2xl">
                <motion.img
                  key={currentImageIndex}
                  src={heroImages[currentImageIndex]}
                  alt="Zalabya Desserts"
                  className="hero-image"
                  initial={{ opacity: 0, scale: 1.1, rotate: 1 }}
                  animate={{ opacity: 1, scale: 1, rotate: 0 }}
                  exit={{ opacity: 0, scale: 0.9, rotate: -1 }}
                  transition={{ duration: 0.7 }}
                />
                <motion.div 
                  className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                />
              </div>
              
              {/* Enhanced Image Indicators */}
              <div className="flex justify-center mt-4 space-x-2">
                {heroImages.map((_, index) => (
                  <motion.button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`w-3 h-3 rounded-full transition-all ${
                      index === currentImageIndex ? 'bg-yellow-400' : 'bg-gray-600'
                    }`}
                    whileHover={{ scale: 1.2 }}
                    whileTap={{ scale: 0.9 }}
                    animate={{
                      scale: index === currentImageIndex ? 1.2 : 1,
                      backgroundColor: index === currentImageIndex ? '#FCD34D' : '#6B7280'
                    }}
                  />
                ))}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Floating Animation Elements */}
        <motion.div
          className="absolute top-1/4 left-10 w-4 h-4 bg-yellow-400 rounded-full opacity-60"
          animate={{
            y: [0, -20, 0],
            opacity: [0.6, 1, 0.6]
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute top-1/3 right-20 w-3 h-3 bg-pink-400 rounded-full opacity-60"
          animate={{
            y: [0, -15, 0],
            x: [0, 10, 0],
            opacity: [0.6, 1, 0.6]
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        />
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gradient mb-4">How We Serve You</h2>
            <p className="text-xl text-gray-300">Choose your preferred way to enjoy our desserts</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                whileHover={{ 
                  scale: 1.05,
                  rotateY: 5,
                  transition: { duration: 0.3 }
                }}
                className="card p-8 text-center"
              >
                <motion.div 
                  className="text-6xl mb-4"
                  animate={{
                    rotate: [0, 10, -10, 0],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: index * 0.5
                  }}
                >
                  {service.icon}
                </motion.div>
                <h3 className="text-2xl font-bold text-yellow-400 mb-4">{service.title}</h3>
                <p className="text-gray-300">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gradient mb-4">Featured Desserts</h2>
            <p className="text-xl text-gray-300">Taste our most popular creations</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {featuredProducts.map((product, index) => (
              <motion.div
                key={product.name}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                whileHover={{ 
                  y: -10,
                  transition: { duration: 0.3 }
                }}
                className="card overflow-hidden"
              >
                <motion.img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-64 object-cover"
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.3 }}
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-yellow-400 mb-2">{product.name}</h3>
                  <p className="text-gray-300">{product.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl font-bold text-gradient mb-4">Visit Us Today</h2>
            <p className="text-xl text-gray-300">Find us in the heart of Mississauga</p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              whileHover={{ scale: 1.05 }}
              className="card p-8 text-center"
            >
              <SafeIcon icon={FiPhone} className="text-4xl text-pink-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-yellow-400 mb-2">Call Us</h3>
              <p className="text-gray-300">+1 (905) 820-7700</p>
              <p className="text-gray-300">+1 (647) 537-0181</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              whileHover={{ scale: 1.05 }}
              className="card p-8 text-center"
            >
              <SafeIcon icon={FiMapPin} className="text-4xl text-pink-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-yellow-400 mb-2">Location</h3>
              <p className="text-gray-300">3700 Eglinton Ave W unit 56</p>
              <p className="text-gray-300">Mississauga, ON L5M 2R9</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              whileHover={{ scale: 1.05 }}
              className="card p-8 text-center"
            >
              <SafeIcon icon={FiClock} className="text-4xl text-pink-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-yellow-400 mb-2">Hours</h3>
              <p className="text-gray-300">Open Daily</p>
              <p className="text-gray-300">Perfect for late dessert</p>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;